<template>
  <div>
    <div v-if="summary.StopCredit === 'Y'" class="sc-banner">
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round">
        <path d="M6 1L11 10.5H1L6 1z" />
        <line x1="6" y1="5" x2="6" y2="7.5" />
        <circle cx="6" cy="9.2" r=".6" fill="currentColor" stroke="none" />
      </svg>
      <span>Stop Credit — credit limit of {{ formattedCreditLimit }} exceeded</span>
    </div>

    <div class="balance-rows">
      <div
        v-for="bucket in visibleBuckets"
        :key="bucket.OriginalIndex"
        class="balance-row"
        @click="onRowClick({ data: bucket })"
      >
        <div class="balance-row-hdr">
          <span class="balance-row-label" :class="{ 'is-zero': bucket.BaseAmount === 0 }">
            {{ bucket.DisplayLabel }}
            <span v-if="bucket.InvoiceCount" class="balance-row-count">· {{ bucket.InvoiceCount }}</span>
          </span>
          <div class="balance-row-amounts">
            <span class="balance-row-amount-primary" :class="{ 'is-overdue': isOverdue(bucket) }">
              {{ formatAmount(bucket.FxAmount) }} {{ summary.FxCurrencyCode }}
            </span>
            <span class="balance-row-amount-secondary">
              {{ formatAmount(bucket.BaseAmount) }} {{ summary.BaseCurrencyCode }}
            </span>
          </div>
        </div>
        <div class="balance-row-track">
          <template v-if="bucket.BaseAmount > 0">
            <div class="balance-row-fill is-overdue" :style="{ width: overduePercent(bucket) + '%' }" />
            <div class="balance-row-fill is-open" :style="{ width: openPercent(bucket) + '%' }" />
          </template>
        </div>
      </div>
    </div>

    <div class="balance-footer">
      <span class="balance-footer-lbl">Total outstanding</span>
      <div class="balance-footer-amounts">
        <span class="balance-footer-amount-primary">{{ formatAmount(totalFxAmount) }} {{ summary.FxCurrencyCode }}</span>
        <span class="balance-footer-amount-secondary">{{ formatAmount(totalBaseAmount) }} {{ summary.BaseCurrencyCode }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { formatNumber } from "devextreme/localization";
import { getDecimalFormat } from "../../_store/helpers";

const BUCKET_TERM_KEYS = ["Current", "Period1", "Period2", "Period3", "Period4"];

export default {
  name: "GcDashboardCustomerBalancesCard",
  props: {
    summary: {
      type: Object,
      required: true,
    },
    selectedIndex: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    formattedCreditLimit() {
      return `$${Number(this.summary.CreditLimit || 0).toLocaleString("en-AU", {
        maximumFractionDigits: 0,
      })}`;
    },
    activeTermType() {
      return this.selectedIndex === 0 ? "Terms" : "Period";
    },
    activeBuckets() {
      return this.selectedIndex === 0
        ? this.summary.AgingByTerms
        : this.summary.AgingByPeriod;
    },
    labeledBuckets() {
      return this.activeBuckets.map((bucket, index) => ({
        ...bucket,
        OriginalIndex: index,
        DisplayLabel:
          this.selectedIndex === 0
            ? this.formatTermsLabel(bucket, index)
            : this.formatPeriodLabel(index),
      }));
    },
    visibleBuckets() {
      if (this.selectedIndex !== 0 || this.summary.CustomerTerms !== 0) {
        return this.labeledBuckets;
      }

      return this.labeledBuckets.filter(
        (bucket) =>
          !(bucket.Label === "N/A" && bucket.BaseAmount === 0 && bucket.FxAmount === 0)
      );
    },
    totalBaseAmount() {
      return this.visibleBuckets.reduce((sum, b) => sum + (Number(b.BaseAmount) || 0), 0);
    },
    totalFxAmount() {
      return this.visibleBuckets.reduce((sum, b) => sum + (Number(b.FxAmount) || 0), 0);
    },
  },
  methods: {
    formatTermsLabel(bucket, index) {
      const terms = this.summary.CustomerTerms;

      if (terms === 0) {
        return bucket.Label;
      }
      if (index === 0) {
        return "Current";
      }
      if (index === 4) {
        return `${terms * 3}+ Days`;
      }

      return `${terms * index} Days`;
    },
    formatPeriodLabel(index) {
      const asOfDate = new Date(this.summary.AsOfDate);
      const monthsBack = index < 4 ? index : 3;
      const target = new Date(asOfDate.getFullYear(), asOfDate.getMonth() - monthsBack, 1);
      const monthYear = target.toLocaleDateString("en-AU", {
        month: "short",
        year: "numeric",
      });

      return index < 4 ? monthYear : `Pre-${monthYear}`;
    },
    onRowClick(e) {
      const term = BUCKET_TERM_KEYS[e.data.OriginalIndex];

      this.$emit("bucket-click", {
        termType: this.activeTermType,
        term,
        bucketLabel: e.data.DisplayLabel,
      });
    },
    formatAmount(value) {
      return formatNumber(Number(value) || 0, getDecimalFormat("Decimal-16-2").ReadOnlyFormat);
    },
    isOverdue(bucket) {
      return bucket.OriginalIndex !== 0 && bucket.BaseAmount > 0;
    },
    overduePercent(bucket) {
      if (!bucket.BaseAmount) return 0;
      return Math.min(100, Math.max(0, (Number(bucket.OverdueAmount) / Number(bucket.BaseAmount)) * 100));
    },
    openPercent(bucket) {
      if (!bucket.BaseAmount) return 0;
      return 100 - this.overduePercent(bucket);
    },
  },
};
</script>

<style lang="scss" scoped>
.sc-banner {
  background: #fff8f8;
  border: 1px solid #f5c6cb;
  color: #842029;
  font-size: 12px;
  font-weight: 500;
  padding: 8px 12px;
  margin-bottom: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    flex-shrink: 0;
  }
}

.balance-rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.balance-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 6px 4px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: rgba(53, 186, 246, 0.04);
  }
}

.balance-row-hdr {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.balance-row-label {
  font-size: 13px;
  font-weight: 600;
  color: #323842;

  &.is-zero {
    font-weight: 500;
    color: #98a2b3;
  }
}

.balance-row-count {
  font-size: 11px;
  font-weight: 400;
  color: #98a2b3;
}

.balance-row-amounts {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
}

.balance-row-amount-primary {
  font-size: 13px;
  font-weight: 600;
  color: #98a2b3;

  &.is-overdue {
    color: #d92d20;
  }
}

.balance-row-amount-secondary {
  font-size: 10.5px;
  color: #98a2b3;
}

.balance-row-track {
  display: flex;
  height: 8px;
  border-radius: 4px;
  background: #eef0f3;
  overflow: hidden;
}

.balance-row-fill {
  height: 100%;

  &.is-overdue {
    background: #d92d20;
  }

  &.is-open {
    background: #eec300;
  }
}

.balance-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  margin-top: 8px;
  border-top: 1px solid var(--base-border-color, #e0e0e0);
}

.balance-footer-lbl {
  font-size: 13px;
  font-weight: 600;
}

.balance-footer-amounts {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
}

.balance-footer-amount-primary {
  font-size: 15px;
  font-weight: 700;
  color: #d92d20;
}

.balance-footer-amount-secondary {
  font-size: 10.5px;
  color: #98a2b3;
}
</style>
